// src/components/Store/ItemStore.jsx

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Stack,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import api from '../../api/axios';
import SafeChip from '../Common/SafeChip';
import { useUser } from '../../contexts/UserContext';

const getFullImageUrl = (url) => (url?.startsWith('http') ? url : `https://sandle.it.com${url}`);

const MOBILE_PAGE_SIZE = 5;
const DESKTOP_PAGE_SIZE = 15;

const ItemStore = () => {
  const { userInfo, refreshUserInfo } = useUser();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const pageSize = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;

  const fetchItems = async () => {
    try {
      const res = await api.get('/api/rewards/items/');
      setItems(res.data.items || []);
    } catch (e) {
      console.error(e);
      alert('아이템 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (id, price) => {
    if (!userInfo) return;

    if ((userInfo?.points ?? 0) < price) {
      alert('Leaf🌿가 부족합니다!');
      return;
    }
    if (!window.confirm('구매하시겠습니까?')) return;

    try {
      await api.post('/api/rewards/items/purchase/', { item_id: id });
      await refreshUserInfo();
      await fetchItems();
      alert('구매 완료!');
    } catch (e) {
      console.error(e);
      alert('구매 실패');
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // 검색/모드 변경 시 페이지 튐 방지
  useEffect(() => {
    setPage(1);
  }, [search, isMobile]);

  const filtered = useMemo(() => {
    const q = (search || '').trim();
    if (!q) return items;
    return items.filter((item) => (item?.name || '').includes(q));
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  // page가 범위를 넘어가면 보정
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  return (
    <Box p={2} sx={{ width: '100%', minWidth: 0 }}>
      {!isMobile && (
        <Typography variant="h5" gutterBottom>
          🎫 쿠폰 상점
        </Typography>
      )}

      {isMobile && (
        <TextField
          fullWidth
          size="small"
          placeholder="아이템 검색"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />
      )}

      {/* =======================
          Mobile: Accordion (5개/페이지)
         ======================= */}
      {isMobile ? (
        <>
          {paginatedItems.map((item) => {
            const isSoldOut = !item.is_unlimited && (item.remaining ?? 0) <= 0;

            return (
              <Accordion key={item.id} sx={{ mb: 1, opacity: isSoldOut ? 0.55 : 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" flexDirection="column" width="100%" sx={{ minWidth: 0 }}>
                    <Typography fontWeight="bold" noWrap>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.price} Leaf🌿
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  {item.image && (
                    <Box mb={1} textAlign="center">
                      <img
                        src={getFullImageUrl(item.image)}
                        alt={item.name}
                        style={{
                          width: '100%',
                          height: 140,
                          objectFit: 'cover',
                          borderRadius: 8,
                        }}
                      />
                    </Box>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                    {item.description || '설명 없음'}
                  </Typography>

                  <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                    {item.is_coupon && <SafeChip label="쿠폰" size="small" />}
                    {item.is_unlimited ? (
                      <SafeChip label="무제한" size="small" />
                    ) : isSoldOut ? (
                      <SafeChip label="매진" size="small" />
                    ) : (
                      <SafeChip label={`남은: ${item.remaining}`} size="small" />
                    )}
                    <SafeChip label={`${item.price} 🌿`} size="small" />
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    size="medium"
                    disabled={isSoldOut || (userInfo?.points ?? 0) < item.price}
                    onClick={() => handlePurchase(item.id, item.price)}
                  >
                    {isSoldOut ? '품절' : (userInfo?.points ?? 0) < item.price ? '부족' : '구매하기'}
                  </Button>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {totalPages > 1 && (
            <Stack mt={2} alignItems="center">
              <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
            </Stack>
          )}
        </>
      ) : (
        <>
          {/* =======================
              Desktop: 5개/줄 고정 + 15개/페이지
             ======================= */}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              width: '100%',
              minWidth: 0,
              gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', // ✅ PC 한줄 5개 고정
            }}
          >
            {paginatedItems.map((item) => {
              const isSoldOut = !item.is_unlimited && (item.remaining ?? 0) <= 0;

              return (
                <Card
                  key={item.id}
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    opacity: isSoldOut ? 0.55 : 1,
                  }}
                >
                  {item.image ? (
                    <CardMedia
                      component="img"
                      image={getFullImageUrl(item.image)}
                      alt={item.name}
                      height="110"
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box sx={{ height: 110 }} />
                  )}

                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, p: '8px 16px 16px' }}>
                    {/* ✅ 이름 2줄 고정 */}
                    <Typography
                      align="center"
                      sx={{
                        fontWeight: 700,
                        maxWidth: '100%',
                        minWidth: 0,
                        wordBreak: 'break-all',
                        overflowWrap: 'anywhere',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: '1.2em',
                        minHeight: '2.4em',
                      }}
                    >
                      {item.name}
                    </Typography>

                    {/* ✅ 설명 2줄 고정 */}
                    <Typography
                      align="center"
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.25,
                        maxWidth: '100%',
                        minWidth: 0,
                        wordBreak: 'break-word',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: '1.2em',
                        minHeight: '2.4em',
                      }}
                    >
                      {item.description || '설명 없음'}
                    </Typography>

                    <Box mt={1} display="flex" justifyContent="center" flexWrap="wrap" gap={0.5}>
                      {item.is_coupon && <SafeChip label="쿠폰" size="small" />}
                      {item.is_unlimited ? (
                        <SafeChip label="무제한" size="small" />
                      ) : isSoldOut ? (
                        <SafeChip label="매진" size="small" />
                      ) : (
                        <SafeChip label={`남은: ${item.remaining}`} size="small" />
                      )}
                      <SafeChip label={`${item.price} 🌿`} size="small" />
                    </Box>

                    <Box mt="auto" display="flex" justifyContent="center" pt={1}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isSoldOut || (userInfo?.points ?? 0) < item.price}
                        onClick={() => handlePurchase(item.id, item.price)}
                        sx={{ width: '100%', maxWidth: 160 }}
                      >
                        {isSoldOut ? '품절' : (userInfo?.points ?? 0) < item.price ? '부족' : '구매하기'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {/* ✅ PC 페이지네이션 */}
          {totalPages > 1 && (
            <Stack mt={3} alignItems="center">
              <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default ItemStore;
